import React, {
    Dispatch,
    ReactNode,
    createContext,
    useContext,
    useMemo,
    useReducer,
} from 'react';
import { usePageProperties } from './PagePropertiesProvider';
import errorReducer, { TAction, IErrorState } from './reducers/ErrorReducer';

export type TPersonalInformationErrorData = {
    FirstName: IErrorState;
    MiddleInitial: IErrorState;
    LastName: IErrorState;
    EmailAddress: IErrorState;
    MailingAddressLine1: IErrorState;
    MailingAddressLine2: IErrorState;
    MailingCity: IErrorState;
    MailingState: IErrorState;
    MailingZipCode: IErrorState;
    PrimaryPhoneNumber: IErrorState;
    Ssn: IErrorState;
    dob: IErrorState;
};

interface IPersonalInformationErrorProviderValue {
    personalInformationError: Record<
        keyof TPersonalInformationErrorData,
        IErrorState
    >;
    dispatch: Dispatch<TAction>;
}

const PersonalInformationErrorContext =
    createContext<IPersonalInformationErrorProviderValue>({
        personalInformationError: {
            FirstName: {
                isInError: false,
                isValidValue: true,
                required: true,
            },
            MiddleInitial: {
                isInError: false,
                isValidValue: true,
                required: false,
            },
            LastName: { isInError: false, isValidValue: true, required: true },
            EmailAddress: {
                isInError: false,
                isValidValue: true,
                required: true,
            },
            MailingAddressLine1: {
                isInError: false,
                isValidValue: true,
                required: true,
            },
            MailingAddressLine2: {
                isInError: false,
                isValidValue: true,
                required: false,
            },
            MailingCity: {
                isInError: false,
                isValidValue: true,
                required: true,
            },
            MailingState: {
                isInError: false,
                isValidValue: true,
                required: true,
            },
            MailingZipCode: {
                isInError: false,
                isValidValue: true,
                required: true,
            },
            PrimaryPhoneNumber: {
                isInError: false,
                isValidValue: true,
                required: false,
            },
            Ssn: { isInError: false, isValidValue: true, required: true },
            dob: { isInError: false, isValidValue: true, required: true },
        },
        dispatch: () => {},
    });

export default function PersonalInformationErrorProvider({
    children,
}: {
    children: ReactNode;
}) {
    const {
        emailRequired,
    } = usePageProperties();
    const [personalInformationError, dispatch] = useReducer(errorReducer, {
        FirstName: {
            isInError: false,
            isValidValue: true,
            required: true,
        },
        MiddleInitial: {
            isInError: false,
            isValidValue: true,
            required: false,
        },
        LastName: {
            isInError: false,
            isValidValue: true,
            required: true,
        },
        EmailAddress: {
            isInError: false,
            isValidValue: true,
            required: emailRequired,
        },
        MailingAddressLine1: {
            isInError: false,
            isValidValue: true,
            required: true,
        },
        MailingAddressLine2: {
            isInError: false,
            isValidValue: true,
            required: false,
        },
        MailingCity: {
            isInError: false,
            isValidValue: true,
            required: true,
        },
        MailingState: {
            isInError: false,
            isValidValue: true,
            required: true,
        },
        MailingZipCode: {
            isInError: false,
            isValidValue: true,
            required: true,
        },
        PrimaryPhoneNumber: {
            isInError: false,
            isValidValue: true,
            required: false,
        },
        Ssn: {
            isInError: false,
            isValidValue: true,
            required: true,
        },
        dob: {
            isInError: false,
            isValidValue: true,
            required: true,
        },
    });

    return (
        <PersonalInformationErrorContext.Provider
            value={useMemo(
                () => ({
                    personalInformationError,
                    dispatch,
                }),
                [personalInformationError, dispatch],
            )}
        >
            {children}
        </PersonalInformationErrorContext.Provider>
    );
}

export const usePersonalInformationError = () =>
    useContext(PersonalInformationErrorContext);
